import { useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { useBookmarkPageContent } from '../contexts/BookmarkPageContext';
import { getReadRoute } from './useReadUrl';
import { getListPathFromPathname } from '../utils/readerRouteUtils';
import { useReaderLayoutMode } from './useUserPreferences';
import { useIsAboveReaderBreakpoint } from '../components/Reader/useReaderBreakpoint';
import {
  getRememberedListScroll,
  setListScrollRestore,
} from '../services/scrollRestoreStore';

/**
 * Returns a function invoked (before the mutation) when the currently-open
 * article is archived/moved/deleted.
 *
 * Behavior depends on the reader layout:
 * - When the reader is a full-screen view over the list — the `2col`
 *   (list-only) preference, or any layout below the reader breakpoint where
 *   the "Back" button is shown — return to the list you came from, restoring
 *   its scroll position. Advancing to the next article here would replace one
 *   full-screen article with another, which is not what the user wants.
 * - In `full` mode (a 3-column user who expanded the reader to full width),
 *   collapse back to the `3col` view and advance the reader pane to the next
 *   article, so the action drops you back into your side-by-side working view.
 * - Otherwise (true side-by-side 3-column mode) advance the reader pane in
 *   place: try index+1 (next), fall back to index-1 (previous), fall back to
 *   the list view if no bookmarks remain.
 *
 * Uses a ref so the callback always reads fresh state from the
 * latest render without needing to be recreated.
 */
export function useNavigateToNextBookmark() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams({ strict: false });
  const bookmarkPage = useBookmarkPageContent();
  const { readerLayoutMode, setReaderLayoutMode } = useReaderLayoutMode();
  const isAboveBreakpoint = useIsAboveReaderBreakpoint();

  const stateRef = useRef({
    pathname,
    params,
    bookmarkPage,
    readerLayoutMode,
    setReaderLayoutMode,
    isAboveBreakpoint,
  });
  stateRef.current = {
    pathname,
    params,
    bookmarkPage,
    readerLayoutMode,
    setReaderLayoutMode,
    isAboveBreakpoint,
  };

  return useCallback(
    (removedBookmarkId: string) => {
      const {
        pathname,
        params,
        bookmarkPage,
        readerLayoutMode,
        setReaderLayoutMode,
        isAboveBreakpoint,
      } = stateRef.current;

      // Full-screen reader over the list (matches ReaderHeader's back-button
      // condition): return to the list instead of advancing to the next article.
      const isFullScreenReaderOverList =
        !isAboveBreakpoint || readerLayoutMode === '2col';
      if (isFullScreenReaderOverList) {
        const listPath = getListPathFromPathname(pathname);
        const remembered = getRememberedListScroll(listPath);
        if (remembered != null) {
          setListScrollRestore({
            scrollTop: remembered,
            targetPath: listPath,
            source: 'reader-action',
            timestamp: Date.now(),
          });
        }
        navigate({ to: listPath as '/', replace: true, search: (prev) => prev });
        return;
      }

      // Expanded to full width within 3-column context: drop back to the
      // side-by-side 3-column view, then advance the reader pane below.
      if (readerLayoutMode === 'full') {
        setReaderLayoutMode('3col');
      }

      if (!bookmarkPage) {
        const listPath = getListPathFromPathname(pathname);
        navigate({ to: listPath as '/' });
        return;
      }

      const { bookmarks, setActiveBookmark } = bookmarkPage;
      const currentIndex = bookmarks.findIndex(
        (b) => String(b.id) === String(removedBookmarkId),
      );

      let nextBookmarkId: string | null = null;
      if (currentIndex !== -1) {
        if (currentIndex + 1 < bookmarks.length) {
          nextBookmarkId = bookmarks[currentIndex + 1].id;
        } else if (currentIndex - 1 >= 0) {
          nextBookmarkId = bookmarks[currentIndex - 1].id;
        }
      }

      if (!nextBookmarkId) {
        // No bookmarks left — go to list view
        const listPath = getListPathFromPathname(pathname);
        navigate({ to: listPath as '/' });
        return;
      }

      const isUrlBased = pathname.includes('/read/');

      if (isUrlBased) {
        const { toPattern, baseParams } = getReadRoute(pathname, params);
        navigate({
          to: toPattern,
          params: { ...baseParams, id: nextBookmarkId },
          search: (prev) => prev,
          replace: true,
        });
      } else {
        setActiveBookmark({ id: nextBookmarkId });
      }
    },
    [navigate],
  );
}
